// src/components/plan/PlanAndDetailsContent.jsx
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Collapse,
  TextField,
  MenuItem,
  CircularProgress,
  Grid,
  Checkbox,
  FormControlLabel,
  Switch,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Stack,
  InputAdornment,
  Snackbar,
  Alert,
} from "@mui/material";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";

import CloseIcon from "@mui/icons-material/Close";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import ExpandLessOutlinedIcon from "@mui/icons-material/ExpandLessOutlined";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import SettingsIcon from "@mui/icons-material/Settings";
import BoundaryIcon from "@mui/icons-material/BorderAll";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import { TestCaseCustomIcon } from "../../../styled/CustomIcons";
import { useApplicationContext } from "../../../../context/ApplicationContext";
import { getAutomationFilesList_API } from "../../../../api/controller/AutomationController";
import { getFolderItemsForLink_API } from "../../../../api/controller/ModuleController";
import { getRegressionPlan_API, updateRegressionPlan_API } from "../../../../api/controller/RegressionController";
import { fetchFieldNames_API, getBoundaryCategories_API } from "../../../../api/controller/AutomationController";
import { font } from "../../../../styles/font";
import { useTheme } from "@emotion/react";
import BoundaryMatrix from "../../../dialog/BoundaryMatrix";

// Boundary Config Dialog Component
function BoundaryConfigDialog({ open, onClose, testCase, onSave, plan, projectName }) {
  const [selectedMatrix, setSelectedMatrix] = useState({});
  const [availableFields, setAvailableFields] = useState([]);
  const [boundaryCategories, setBoundaryCategories] = useState([]);
  const [loadingFields, setLoadingFields] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState("");
  const theme = useTheme();

  const convertSelectionsToMatrix = useCallback((selections) => {
    const matrix = {};
    if (selections && Array.isArray(selections)) {
      selections.forEach(selection => {
        if (selection.fieldName) {
          const categories = Array.isArray(selection.categories) ? selection.categories : [];
          if (categories.length > 0) {
            matrix[selection.fieldName] = categories;
          }
        }
      });
    }
    return matrix;
  }, []);

  const convertMatrixToBoundarySelections = useCallback((matrix) => {
    return Object.entries(matrix)
      .filter(([_, categories]) => categories && categories.length > 0)
      .map(([fieldName, categories]) => ({
        fieldName,
        categories: categories
      }));
  }, []);

  const fetchBoundaryCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await getBoundaryCategories_API();
      console.log("Boundary categories response:", response);
      if (response && response.data) {
        const categories = Array.isArray(response.data) 
          ? response.data.map((cat) => (typeof cat === 'object' ? cat.name : cat))
          : [];
        setBoundaryCategories(categories);
      }
    } catch (error) {
      console.error("Error fetching boundary categories:", error);
      setError("Failed to load boundary categories.");
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchAvailableFields = async () => {
    if (!testCase || !projectName) return;
    
    setLoadingFields(true);
    try {
      const scriptFileId = testCase.selectedFiles?.SCRIPT?.fileId || 
                          testCase.scriptFileId ||
                          testCase.selectedFiles?.scriptFileId;
      
      if (!scriptFileId) {
        console.warn("No script file ID found for test case:", testCase.customId);
        setAvailableFields([]);
        return;
      }
      
      const requestData = {
        customId: testCase.customId,
        projectName: projectName,
        folderPath: testCase.folderPath,
        section: "TestCase",
        fileId: scriptFileId,
      };
      
      console.log("Fetching fields with request:", requestData);
      const data = await fetchFieldNames_API(requestData);
      console.log("Fields API response:", data);
      
      const fieldsArray = Array.isArray(data?.fieldNames) 
        ? data.fieldNames 
        : (Array.isArray(data) ? data : []);
        
      setAvailableFields(fieldsArray);
    } catch (error) {
      console.error("Failed to fetch script fields:", error);
      setAvailableFields([]);
    } finally {
      setLoadingFields(false);
    }
  };

  useEffect(() => {
    if (open && testCase) {
      const existingConfig = testCase.boundaryFieldCategorySelections || 
                            testCase.boundaryConfig || 
                            [];
      
      console.log("Loading existing config for", testCase.customId, ":", existingConfig);
      const matrix = convertSelectionsToMatrix(existingConfig);
      setSelectedMatrix(matrix);
      
      fetchBoundaryCategories();
      fetchAvailableFields();
    }
  }, [open, testCase, convertSelectionsToMatrix]);

  const handleSave = async () => {
    setSaving(true);
    const boundarySelections = convertMatrixToBoundarySelections(selectedMatrix);
    console.log("Saving boundary selections:", boundarySelections);
    await onSave(boundarySelections);
    setSaving(false);
    onClose();
  };

  const configuredFieldsCount = Object.keys(selectedMatrix).filter(
    field => selectedMatrix[field]?.length > 0
  ).length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ p: 2.5, borderBottom: '1px solid #e0e0e0' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1.5} alignItems="center">
            <BoundaryIcon color="primary" sx={{ fontSize: 28 }} />
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>Boundary Configuration</Typography>
              <Typography variant="caption" color="text.secondary">
                Project: {projectName} • {testCase?.customId}
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search fields..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>),
            }}
          />
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {loadingFields || loadingCategories ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <BoundaryMatrix
              fields={availableFields}
              categories={boundaryCategories}
              selectedMatrix={selectedMatrix}
              onMatrixChange={setSelectedMatrix}
              loading={loadingFields || loadingCategories}
              searchTerm={searchTerm}
            />
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2.5, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleSave} 
          variant="contained"
          disabled={saving || configuredFieldsCount === 0}
        >
          {saving ? <CircularProgress size={24} /> : `Save ${configuredFieldsCount} Configured Field(s)`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Automation Files Dropdown Component
function AutomationFilesDropdown({ files, selectedFiles, setSelectedFiles, loading = false }) {
  const fileTypes = ["ELEMENT", "SCRIPT", "TESTDATA", "APPLICATION"];

  if (loading) {
    return (
      <Box sx={{ ml: 4, display: "flex", alignItems: "center", gap: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">Loading...</Typography>
      </Box>
    );
  }

  const handleSelectChange = (type, fileId) => {
    const options = [];
    if (files[type]?.local) options.push(files[type].local);
    if (files[type]?.inherited?.length > 0) options.push(...files[type].inherited);
    const selected = options.find((f) => f.fileId.toString() === fileId);
    setSelectedFiles((prev) => ({ ...prev, [type]: selected }));
  };

  return (
    <Box sx={{ ml: 4, mt: 1 }}>
      <Grid container spacing={2}>
        {fileTypes.map((type) => {
          const options = [];
          if (files[type]?.local) options.push(files[type].local);
          if (files[type]?.inherited?.length > 0) options.push(...files[type].inherited);
          if (options.length === 0) return null;

          return (
            <Grid item xs={6} key={type}>
              <TextField
                select
                size="small"
                fullWidth
                value={selectedFiles[type]?.fileId?.toString() || ""}
                onChange={(e) => handleSelectChange(type, e.target.value)}
                label={type.charAt(0) + type.slice(1).toLowerCase()}
                variant="outlined"
              >
                {options.map((f) => (
                  <MenuItem key={f.fileId} value={f.fileId.toString()}>
                    {f.name || type}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}

// Recursive Folder Component
function RecursiveFolder({
  folder,
  foldersExpanded,
  setFoldersExpanded,
  selectedTestCases,
  setSelectedTestCases,
  automationFiles,
  fetchAutomationFiles,
  level = 0,
}) {
  if (!folder.children || folder.children.length === 0) return null;

  const toggleFolderExpand = (name) => {
    setFoldersExpanded((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const isTestCaseSelected = (tc) =>
    selectedTestCases.some((t) => t.customId === tc.customId);

  const handleTestCaseCheckbox = async (tc, checked) => {
    if (checked) {
      if (!isTestCaseSelected(tc)) {
        setSelectedTestCases((prev) => [...prev, { 
          ...tc, 
          selectedFiles: {}, 
          boundaryEnabled: false, 
          boundaryConfig: [], 
          boundaryFieldCategorySelections: [] 
        }]);
        await fetchAutomationFiles(tc);
      }
    } else {
      setSelectedTestCases((prev) => prev.filter((t) => t.customId !== tc.customId));
    }
  };

  const handleFolderCheckbox = async (folder, checked) => {
    const childrenTCs = folder.children.filter((c) => c.type === "TestCase");
    if (checked) {
      const newTCs = childrenTCs.filter((tc) => !isTestCaseSelected(tc));
      setSelectedTestCases((prev) => [
        ...prev,
        ...newTCs.map((tc) => ({ 
          ...tc, 
          selectedFiles: {}, 
          boundaryEnabled: false, 
          boundaryConfig: [], 
          boundaryFieldCategorySelections: [] 
        })),
      ]);
      for (const tc of newTCs) await fetchAutomationFiles(tc);
    } else {
      const idsToRemove = childrenTCs.map((tc) => tc.customId);
      setSelectedTestCases((prev) => prev.filter((t) => !idsToRemove.includes(t.customId)));
    }
  };

  const folderTCs = folder.children.filter((c) => c.type === "TestCase");
  const folderChecked = folderTCs.length > 0 && folderTCs.every((tc) => isTestCaseSelected(tc));
  const folderIndeterminate = folderTCs.some((tc) => isTestCaseSelected(tc)) && !folderChecked;

  return (
    <Box sx={{ ml: level * 2 }}>
      <Box sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        px: 1.5,
        py: 1,
        borderRadius: theme.shape.borderRadius,
        backgroundColor: theme.palette.highlight.main,
        cursor: "pointer",
        "&:hover": { backgroundColor: theme.palette.highlight.light },
      })}>
        <Checkbox
          checked={folderChecked}
          indeterminate={folderIndeterminate}
          onChange={(e) => handleFolderCheckbox(folder, e.target.checked)}
          onClick={(e) => e.stopPropagation()}
        />
        <Box onClick={(e) => { e.stopPropagation(); toggleFolderExpand(folder.path); }} sx={{ mr: 0.5 }}>
          {foldersExpanded[folder.path] ? <ExpandLessOutlinedIcon fontSize="small" /> : <ExpandMoreOutlinedIcon fontSize="small" />}
        </Box>
        <FolderOutlinedIcon fontSize="small" sx={(theme) => ({ mr: 1, color: theme.palette.primary.main })} />
        <Typography sx={{ fontWeight: 500 }}>{folder.name}</Typography>
        <Typography sx={{ ml: "auto", fontSize: font.size.fs_6 }}>[{folder.children?.length || 0}]</Typography>
      </Box>

      <Collapse in={foldersExpanded[folder.path]} timeout="auto" unmountOnExit>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 0.5 }}>
          {folder.children.map((child) => {
            if (child.type === "TestCase") {
              const checked = isTestCaseSelected(child);
              return (
                <Box key={child.customId}>
                  <Box sx={(theme) => ({
                    display: "flex",
                    alignItems: "center",
                    mb: 0.75,
                    px: 1.25,
                    py: 0.75,
                    ml: 3,
                    gap: 0.75,
                    borderRadius: theme.shape.borderRadius,
                    backgroundColor: theme.palette.background.custom,
                    cursor: "pointer",
                    "&:hover": { backgroundColor: theme.palette.highlight.light },
                  })}>
                    <Checkbox checked={checked} onChange={(e) => handleTestCaseCheckbox(child, e.target.checked)} />
                    <TestCaseCustomIcon fontSize="small" sx={(theme) => ({ mr: 1, color: theme.palette.secondary.main })} />
                    <Typography variant="body2">{child.customId} – {child.title}</Typography>
                  </Box>
                </Box>
              );
            } else if (child.type === "Folders") {
              return (
                <RecursiveFolder
                  key={child.path}
                  folder={child}
                  foldersExpanded={foldersExpanded}
                  setFoldersExpanded={setFoldersExpanded}
                  selectedTestCases={selectedTestCases}
                  setSelectedTestCases={setSelectedTestCases}
                  automationFiles={automationFiles}
                  fetchAutomationFiles={fetchAutomationFiles}
                  level={level + 1}
                />
              );
            }
            return null;
          })}
        </Box>
      </Collapse>
    </Box>
  );
}

// Main Component
export default function PlanAndDetailsContent({ 
  onChange, 
  plan, 
  onAddTestCases, 
  onPlanUpdate,
  initialTestCases = []
}) {
  const { activeProject, handleSnackbar } = useApplicationContext();
  const theme = useTheme();

  const [folders, setFolders] = useState([]);
  const [automationFiles, setAutomationFiles] = useState({});
  const [selectedTestCases, setSelectedTestCases] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState({});
  const [foldersExpanded, setFoldersExpanded] = useState({});
  const [expandedRightTCs, setExpandedRightTCs] = useState({});
  const [draggingIndex, setDraggingIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [boundaryConfigOpen, setBoundaryConfigOpen] = useState(false);
  const [currentBoundaryTC, setCurrentBoundaryTC] = useState(null);
  const [bulkBoundaryMode, setBulkBoundaryMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  
  // Refs to prevent infinite loops
  const isInitialLoad = useRef(true);
  const isAutoSaving = useRef(false);
  const lastSavedData = useRef(null);

  const getAllTestCases = (folders) => {
    const result = [];
    const traverse = (folderList) => {
      folderList.forEach((f) => {
        f.children?.forEach((c) => {
          if (c.type === "TestCase") result.push(c);
          else if (c.type === "Folders") traverse([c]);
        });
      });
    };
    traverse(folders);
    return result;
  };
  
  const allTCs = getAllTestCases(folders);
  const allTestCasesSelected = allTCs.length > 0 && allTCs.every((tc) => 
    selectedTestCases.some((t) => t.customId === tc.customId)
  );

  const countTestCases = (folder) => {
    if (!folder.children) return 0;
    return folder.children.reduce((acc, child) => {
      if (child.type === "TestCase") return acc + 1;
      if (child.type === "Folders") return acc + countTestCases(child);
      return acc;
    }, 0);
  };

  // Fetch folders
  useEffect(() => {
    if (!activeProject?.name) return;
    const fetchFolders = async () => {
      try {
        const query = `name=${encodeURIComponent(activeProject.name)}`;
        const response = await getFolderItemsForLink_API(query);
        if (response?.data)
          setFolders(response.data.testCases.filter((f) => f.children?.length > 0));
      } catch (err) {
        console.error("Failed to fetch folders", err);
      }
    };
    fetchFolders();
  }, [activeProject]);

  // Fetch automation files for a test case
  const fetchAutomationFiles = async (tc) => {
    setLoadingFiles((prev) => ({ ...prev, [tc.customId]: true }));
    const fileTypes = ["ELEMENT", "SCRIPT", "TESTDATA", "APPLICATION"];
    const files = {};

    for (const type of fileTypes) {
      try {
        const res = await getAutomationFilesList_API({
          projectName: activeProject.name,
          folderPath: tc.folderPath,
          customId: tc.customId,
          fileType: type,
          section: "TestCase",
        });
        files[type] = (res?.data?.local || (res?.data?.inherited?.length ?? 0) > 0) ? res.data : { local: null, inherited: [] };
      } catch {
        files[type] = { local: null, inherited: [] };
      }
    }

    setAutomationFiles((prev) => ({ ...prev, [tc.customId]: files }));

    const initialSelectedFiles = {};
    fileTypes.forEach((t) => {
      if (files[t]?.local) initialSelectedFiles[t] = files[t].local;
      else if (files[t]?.inherited?.length > 0) initialSelectedFiles[t] = files[t].inherited[0];
    });

    setSelectedTestCases((prev) =>
      prev.map((t) => t.customId === tc.customId ? { ...t, selectedFiles: initialSelectedFiles } : t)
    );

    setLoadingFiles((prev) => ({ ...prev, [tc.customId]: false }));
  };

// In PlanAndDetailsContent.jsx, update the saveToAPI function to call the parent's onChange

const saveToAPI = async (testCases, showSnackbar = false) => {
  if (!plan?.name || !activeProject?.name) return;
  
  if (isAutoSaving.current) return;
  
  const currentDataStr = JSON.stringify(testCases.map(tc => ({
    id: tc.customId,
    boundary: tc.boundaryFieldCategorySelections || tc.boundaryConfig,
    files: tc.selectedFiles
  })));
  
  if (lastSavedData.current === currentDataStr) {
    console.log("No changes detected, skipping save");
    return;
  }
  
  isAutoSaving.current = true;
  setIsSaving(true);
  
  try {
    const formattedTestCases = testCases.map((tc) => {
      // Ensure boundary config is properly formatted
      let boundarySelections = tc.boundaryFieldCategorySelections || tc.boundaryConfig || [];
      
      boundarySelections = boundarySelections.map(selection => ({
        fieldName: selection.fieldName || selection.field,
        categories: Array.isArray(selection.categories) ? selection.categories : 
                   (selection.category ? [selection.category] : [])
      })).filter(selection => selection.fieldName && selection.categories.length > 0);
      
      return {
        testCaseCustomId: tc.customId,
        folderPath: tc.folderPath || "",
        elementFileId: tc.selectedFiles?.ELEMENT?.fileId || null,
        applicationFileId: tc.selectedFiles?.APPLICATION?.fileId || null,
        testDataFileId: tc.selectedFiles?.TESTDATA?.fileId || null,
        scriptFileId: tc.selectedFiles?.SCRIPT?.fileId || null,
        boundaryFieldCategorySelections: boundarySelections
      };
    });

    const updatePayload = {
      name: plan.name,
      projectName: activeProject.name,
      testCases: formattedTestCases
    };

    if (plan.lastModifiedAt) {
      updatePayload.lastModifiedAt = plan.lastModifiedAt;
    }

    console.log("Saving payload:", updatePayload);
    const response = await updateRegressionPlan_API(updatePayload);
    
    if (response?.status === 200 || response?.status === 201 || response?.data) {
      if (response.data?.lastModifiedAt && onPlanUpdate) {
        onPlanUpdate({ ...plan, lastModifiedAt: response.data.lastModifiedAt });
      }
      lastSavedData.current = currentDataStr;
      if (showSnackbar) {
        setSnackbar({ open: true, message: "Changes saved successfully", severity: "success" });
      }
    }
    
    // CRITICAL: Pass the complete test case objects to parent via onChange
    // This ensures the parent has all boundary configuration data
    if (onChange) {
      onChange({ testCases: testCases.map(tc => ({
        ...tc,
        // Ensure boundary config is preserved in the parent's state
        boundaryFieldCategorySelections: tc.boundaryFieldCategorySelections || tc.boundaryConfig,
        boundaryConfig: tc.boundaryConfig || tc.boundaryFieldCategorySelections
      })) });
    }
  } catch (error) {
    console.error("Failed to save regression plan:", error);
    if (showSnackbar) {
      setSnackbar({ open: true, message: error.response?.data?.message || "Failed to save changes", severity: "error" });
    }
  } finally {
    setIsSaving(false);
    isAutoSaving.current = false;
  }
};

  // Load plan test cases from API
  const loadPlanTestCases = async () => {
    if (!plan?.name || !activeProject?.name) return;
    
    try {
      console.log("Fetching regression plan:", plan.name, "for project:", activeProject.name);
      const response = await getRegressionPlan_API({
        name: plan.name,
        projectName: activeProject.name,
      });
      
      console.log("getRegressionPlan_API full response:", response);
      
      // Extract plan data from response
      let planData = null;
      if (response?.data) {
        planData = response.data;
      } else if (response?.data?.data) {
        planData = response.data.data;
      } else if (response?.testCases) {
        planData = response;
      }
      
      console.log("Parsed plan data:", planData);
      
      const apiTestCases = planData?.testCases || [];
      console.log("API Test Cases:", apiTestCases);
      
      const formatted = apiTestCases.map((tc) => {
        // CRITICAL FIX: Extract boundary config from the correct location
        let boundaryConfig = [];
        
        // The API returns boundaryFieldCategorySelections directly on the test case object
        if (tc.boundaryFieldCategorySelections && Array.isArray(tc.boundaryFieldCategorySelections)) {
          boundaryConfig = tc.boundaryFieldCategorySelections;
          console.log(`Found boundary config for ${tc.testCaseCustomId}:`, boundaryConfig);
        } else if (tc.boundaryConfig && Array.isArray(tc.boundaryConfig)) {
          boundaryConfig = tc.boundaryConfig;
          console.log(`Found boundary config (legacy) for ${tc.testCaseCustomId}:`, boundaryConfig);
        } else {
          console.log(`No boundary config found for ${tc.testCaseCustomId}`);
        }
        
        // Map file objects from API response
        // The API returns file objects directly with fileId property
        const elementFile = tc.elementFile || (tc.elementFileId ? { fileId: tc.elementFileId } : null);
        const applicationFile = tc.applicationFile || (tc.applicationFileId ? { fileId: tc.applicationFileId } : null);
        const testDataFile = tc.testDataFile || (tc.testDataFileId ? { fileId: tc.testDataFileId } : null);
        const scriptFile = tc.scriptFile || (tc.scriptFileId ? { fileId: tc.scriptFileId } : null);
        
        return {
          customId: tc.testCaseCustomId || tc.customId,
          title: tc.testCaseTitle || tc.title || "",
          folderPath: tc.folderPath || "",
          selectedFiles: {
            ELEMENT: elementFile,
            APPLICATION: applicationFile,
            TESTDATA: testDataFile,
            SCRIPT: scriptFile,
          },
          boundaryEnabled: boundaryConfig && boundaryConfig.length > 0,
          boundaryConfig: boundaryConfig,
          boundaryFieldCategorySelections: boundaryConfig,
        };
      });
      
      console.log("Formatted test cases with boundary config:", formatted);
      setSelectedTestCases(formatted);
      
      // Fetch automation files for each test case
      for (const tc of formatted) {
        await fetchAutomationFiles(tc);
      }

    } catch (err) {
      console.error("Failed to load regression plan:", err);
      // if (handleSnackbar) {
      //   handleSnackbar("Failed to load regression plan: " + (err.message || "Unknown error"), "error");
      // }
    }
  };

  // Initialize component
  useEffect(() => {
    if (!activeProject?.name) return;

    const initialize = async () => {
      isInitialLoad.current = true;
      
      if (initialTestCases && initialTestCases.length > 0) {
        console.log("Using initial test cases from parent:", initialTestCases);
        setSelectedTestCases(initialTestCases);
        for (const tc of initialTestCases) {
          await fetchAutomationFiles(tc);
        }
      } else if (plan?.name) {
        await loadPlanTestCases();
      }
      
      setTimeout(() => {
        isInitialLoad.current = false;
      }, 500);
    };

    initialize();
  }, [activeProject?.name, plan?.name]);

  // Auto-save debounced
  const debouncedSave = useRef(null);
  
  const triggerAutoSave = useCallback((testCases) => {
    if (debouncedSave.current) {
      clearTimeout(debouncedSave.current);
    }
    debouncedSave.current = setTimeout(() => {
      if (!isInitialLoad.current) {
        saveToAPI(testCases, false);
      }
    }, 1500);
  }, []);

  useEffect(() => {
    if (!isInitialLoad.current && selectedTestCases.length > 0 && plan?.name) {
      triggerAutoSave(selectedTestCases);
    }
    
    return () => {
      if (debouncedSave.current) {
        clearTimeout(debouncedSave.current);
      }
    };
  }, [selectedTestCases, plan?.name, triggerAutoSave]);

  const handleGlobalCheckbox = async (checked) => {
    if (checked) {
      const newTCs = allTCs.filter((tc) => !selectedTestCases.some((t) => t.customId === tc.customId));
      setSelectedTestCases((prev) => [
        ...prev,
        ...newTCs.map((tc) => ({ 
          ...tc, 
          selectedFiles: {}, 
          boundaryEnabled: false, 
          boundaryConfig: [], 
          boundaryFieldCategorySelections: [] 
        })),
      ]);
      for (const tc of newTCs) await fetchAutomationFiles(tc);
    } else {
      const allIds = allTCs.map((tc) => tc.customId);
      setSelectedTestCases((prev) => prev.filter((t) => !allIds.includes(t.customId)));
    }
  };

  const toggleRightTCExpand = (tcId) => setExpandedRightTCs((prev) => ({ ...prev, [tcId]: !prev[tcId] }));

  const removeRightTC = (tcId) => {
    const updated = selectedTestCases.filter((t) => t.customId !== tcId);
    setSelectedTestCases(updated);
    onAddTestCases?.(updated);
  };

  // Drag and Drop
  const handleDragStart = (e, index) => {
    e.dataTransfer.setData("text/plain", index.toString());
    setDraggingIndex(index);
    e.currentTarget.style.opacity = "0.5";
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    setDragOverIndex(index);
    e.currentTarget.style.borderTop = "2px solid #2196f3";
  };

  const handleDragLeave = (e) => {
    e.currentTarget.style.borderTop = "";
    setDragOverIndex(null);
  };

  const handleDrop = (e, index) => {
    e.preventDefault();
    e.currentTarget.style.borderTop = "";
    const draggedIndex = parseInt(e.dataTransfer.getData("text/plain"));
    if (draggedIndex !== null && draggedIndex !== index) {
      const items = [...selectedTestCases];
      const [draggedItem] = items.splice(draggedIndex, 1);
      items.splice(index, 0, draggedItem);
      setSelectedTestCases(items);
      onAddTestCases?.(items);
    }
    setDraggingIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = (e) => {
    if (e.currentTarget) e.currentTarget.style.opacity = "1";
    setDraggingIndex(null);
    setDragOverIndex(null);
  };

  const handleBoundaryToggle = (tcId, enabled) => {
    setSelectedTestCases((prev) => prev.map((t) => 
      t.customId === tcId ? { ...t, boundaryEnabled: enabled } : t
    ));
  };

  const handleBulkBoundaryToggle = (enabled) => {
    setSelectedTestCases((prev) => prev.map((t) => ({ ...t, boundaryEnabled: enabled })));
  };

  const handleBoundaryConfigSave = async (boundarySelections) => {
    console.log("Saving boundary selections for", currentBoundaryTC?.customId, ":", boundarySelections);
    
    const updatedTestCases = selectedTestCases.map(tc =>
      tc.customId === currentBoundaryTC?.customId
        ? {
            ...tc,
            boundaryFieldCategorySelections: boundarySelections,
            boundaryConfig: boundarySelections,
            boundaryEnabled: boundarySelections && boundarySelections.length > 0
          }
        : tc
    );
    
    setSelectedTestCases(updatedTestCases);
    await saveToAPI(updatedTestCases, true);
  };

  const getBoundarySummary = (tc) => {
    if (!tc.boundaryEnabled) return "Disabled";
    
    const boundaryConfig = tc.boundaryFieldCategorySelections || tc.boundaryConfig || [];
    
    if (!boundaryConfig || boundaryConfig.length === 0) return "No fields configured";
    
    const totalCategories = boundaryConfig.reduce((sum, field) => {
      const categories = field.categories || [];
      return sum + categories.length;
    }, 0);
    
    return `${boundaryConfig.length} field(s), ${totalCategories} condition(s)`;
  };

  const openBoundaryConfig = (tc) => {
    const latestTC = selectedTestCases.find(t => t.customId === tc.customId);
    setCurrentBoundaryTC(latestTC || tc);
    setBoundaryConfigOpen(true);
  };

  const boundaryEnabledCount = selectedTestCases.filter(tc => tc.boundaryEnabled).length;

  // Manual refresh button handler
  const handleRefresh = async () => {
    await loadPlanTestCases();
  };

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", borderBottom: "0" }}>
      <BoundaryConfigDialog
        open={boundaryConfigOpen}
        onClose={() => setBoundaryConfigOpen(false)}
        testCase={currentBoundaryTC}
        onSave={handleBoundaryConfigSave}
        plan={plan}
        projectName={activeProject?.name}
      />

      {isSaving && (
        <Box sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 9999 }}>
          <Chip icon={<CircularProgress size={16} />} label="Saving..." color="primary" size="small" />
        </Box>
      )}

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={3000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })} 
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>

      <Box sx={{ flex: 1, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, p: 2, height: "100%", overflow: "hidden" }}>
        {/* Left Panel */}
        <Paper elevation={0} sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Checkbox
                checked={allTestCasesSelected}
                indeterminate={selectedTestCases.length > 0 && !allTestCasesSelected}
                onChange={(e) => handleGlobalCheckbox(e.target.checked)}
              />
              <Typography sx={{ fontWeight: 600 }}>
                Available Test Cases ({folders.reduce((acc, f) => acc + countTestCases(f), 0)})
              </Typography>
            </Box>
            <Tooltip title="Refresh from server">
              <IconButton size="small" onClick={handleRefresh}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ flex: 1, overflowY: "auto", px: 2, py: 1.5 }}>
            {folders.map((folder) => (
              <RecursiveFolder
                key={folder.path}
                folder={folder}
                foldersExpanded={foldersExpanded}
                setFoldersExpanded={setFoldersExpanded}
                selectedTestCases={selectedTestCases}
                setSelectedTestCases={setSelectedTestCases}
                automationFiles={automationFiles}
                fetchAutomationFiles={fetchAutomationFiles}
              />
            ))}
          </Box>
        </Paper>

        {/* Right Panel */}
        <Paper sx={{ flex: 1, p: 2, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Typography fontWeight={600}>Selected Test Cases ({selectedTestCases.length})</Typography>
            {selectedTestCases.length > 0 && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography variant="caption">Boundary enabled: {boundaryEnabledCount}/{selectedTestCases.length}</Typography>
                <FormControlLabel
                  control={
                    <Switch 
                      size="small" 
                      checked={bulkBoundaryMode} 
                      onChange={(e) => { 
                        setBulkBoundaryMode(e.target.checked); 
                        if (e.target.checked) handleBulkBoundaryToggle(true); 
                      }} 
                    />
                  }
                  label="Enable All Boundary"
                />
              </Box>
            )}
          </Box>

          <Box sx={{ flex: 1, overflowY: "auto" }}>
            {selectedTestCases.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mt: 4, fontStyle: "italic" }}>
                No test cases selected. Select test cases from the left panel.
              </Typography>
            ) : (
              selectedTestCases.map((tc, index) => {
                const files = automationFiles[tc.customId] || {};
                const isExpanded = expandedRightTCs[tc.customId];
                const boundarySummary = getBoundarySummary(tc);
                const hasBoundaryConfig = (tc.boundaryFieldCategorySelections?.length > 0) || (tc.boundaryConfig?.length > 0);

                return (
                  <Box
                    key={tc.customId}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, index)}
                    onDragEnd={handleDragEnd}
                    sx={{
                      mb: 1,
                      p: 1.5,
                      borderRadius: 1,
                      backgroundColor: draggingIndex === index ? theme.palette.action.selected : dragOverIndex === index ? theme.palette.action.hover : theme.palette.background.paper,
                      border: draggingIndex === index ? `2px solid ${theme.palette.primary.main}` : dragOverIndex === index ? `2px dashed ${theme.palette.primary.main}` : 'none',
                      cursor: "grab",
                      '&:active': { cursor: "grabbing" }
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1 }}>
                        <DragIndicatorIcon fontSize="small" />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{index + 1}.</Typography>
                        <IconButton size="small" onClick={() => toggleRightTCExpand(tc.customId)}>
                          {isExpanded ? <ExpandLessOutlinedIcon /> : <ExpandMoreOutlinedIcon />}
                        </IconButton>
                        <TestCaseCustomIcon sx={{ color: theme.palette.primary.main }} />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{tc.customId} - {tc.title}</Typography>
                        {hasBoundaryConfig && (
                          <Tooltip title={boundarySummary}>
                            <Chip 
                              icon={<BoundaryIcon />} 
                              label={`${(tc.boundaryFieldCategorySelections || tc.boundaryConfig || []).length}`} 
                              size="small" 
                              color={tc.boundaryEnabled ? "primary" : "default"} 
                              variant={tc.boundaryEnabled ? "filled" : "outlined"} 
                              sx={{ ml: 1, height: 24 }} 
                            />
                          </Tooltip>
                        )}
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Tooltip title={hasBoundaryConfig ? "Enable/Disable Boundary Testing" : "Configure boundary conditions first"}>
                          <span>
                            <Switch 
                              size="small" 
                              checked={tc.boundaryEnabled || false} 
                              onChange={(e) => handleBoundaryToggle(tc.customId, e.target.checked)} 
                              disabled={!hasBoundaryConfig} 
                            />
                          </span>
                        </Tooltip>
                        <Tooltip title="Configure Boundary Conditions">
                          <IconButton 
                            size="small" 
                            onClick={() => openBoundaryConfig(tc)} 
                            color={hasBoundaryConfig ? "primary" : "default"}
                          >
                            <SettingsIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <IconButton size="small" onClick={() => removeRightTC(tc.customId)}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>

                    {/* {hasBoundaryConfig && (
                      <Box sx={{ ml: 6, mb: 1 }}>
                        <Typography variant="caption" color="text.secondary">Boundary: {boundarySummary}</Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                          {(tc.boundaryFieldCategorySelections || tc.boundaryConfig || []).slice(0, 3).map((field) => (
                            <Chip 
                              key={field.fieldName} 
                              label={`${field.fieldName} (${field.categories?.length || 0})`} 
                              size="small" 
                              variant="outlined" 
                              sx={{ height: 20, fontSize: "0.7rem" }} 
                            />
                          ))}
                          {(tc.boundaryFieldCategorySelections || tc.boundaryConfig || []).length > 3 && (
                            <Chip 
                              label={`+${(tc.boundaryFieldCategorySelections || tc.boundaryConfig || []).length - 3} more`} 
                              size="small" 
                              variant="outlined" 
                              sx={{ height: 20, fontSize: "0.7rem" }} 
                            />
                          )}
                        </Box>
                      </Box>
                    )} */}

                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <AutomationFilesDropdown
                        files={files}
                        selectedFiles={tc.selectedFiles || {}}
                        setSelectedFiles={(updated) => {
                          setSelectedTestCases((prev) => 
                            prev.map((t) => t.customId === tc.customId ? { ...t, selectedFiles: updated } : t)
                          );
                        }}
                        loading={loadingFiles[tc.customId]}
                      />
                    </Collapse>
                  </Box>
                );
              })
            )}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}